import type {
  Milestone,
  MilestoneDeliverable,
  Project,
  Task,
  User,
  UserScopedRelationship,
} from '../types';
import type { LegacyPersonaRoleKey } from '../types/legacy/roles';
import type { MsiMetric, ObvPolicy } from '../types/gatePolicy';
import { ensureDemoAvatar } from './demoAvatars';

/**
 * The only demo dataset presented by AX1 Capital.
 *
 * Keep demo records here so every feature tells one coherent governance story.
 * Production data continues to come from Supabase and is never migrated by this file.
 */
export const SHOWCASE_DATASET_VERSION = '2026.07.flagship-v1';
export const SHOWCASE_WORKSPACE_ID = 'ax1-showcase-workspace';
export const SHOWCASE_BLANK_MILESTONE_ID = 'milestone-1-journey-blank';
export const SHOWCASE_FEATURED_OBV_MILESTONE_ID = 'milestone-1-gate';

/** The OBV references this existing Gate outcome; it never creates a parallel KPI. */
export const SHOWCASE_FEATURED_OBV_METRICS: MsiMetric[] = [{
  id: 'milestone-1-gate-delivery', name: 'Verified milestone delivery', weight: 100,
  source: 'computed', scoring: 'target_vs_actual', target: 100, required: true,
  inputBinding: { kind: 'manual_validator', unit: '%', verificationRequired: true, staleAfterHours: 48 },
}];

/** Shared showcase OBV terms. The locked Gate policy and investor surface read this same object. */
export const SHOWCASE_FEATURED_OBV_POLICY: ObvPolicy = {
  enabled: true,
  alignmentVersion: 1,
  totalBudget: 150_000,
  currency: 'EUR',
  budgetLines: [
    { id: 'obv-media', category: 'Site operations', capAmount: 100_000, currency: 'EUR', whitelistedPayees: ['approved-site-vendor'], active: true,
      outcomeLink: { kind: 'metric', metricId: 'milestone-1-gate-delivery' }, control: { kind: 'progress_checkpoint', minimumProgressPercent: 70 }, initialAllowance: 10_000 },
    { id: 'obv-tools', category: 'Tools & equipment', capAmount: 20_000, currency: 'EUR', whitelistedPayees: ['approved-equipment-vendor'], active: true,
      outcomeLink: { kind: 'milestone', rationale: 'Equipment required for the agreed foundation works.' }, control: { kind: 'cap_only' }, initialAllowance: 0 },
    { id: 'obv-delivery', category: 'Delivery support', capAmount: 30_000, currency: 'EUR', whitelistedPayees: ['approved-service-partner'], active: true,
      outcomeLink: { kind: 'milestone', rationale: 'Operational support for the existing delivery plan; not a second delivery fee.' }, control: { kind: 'cap_only' }, initialAllowance: 0 },
  ],
  totalCap: 150_000,
  cadence: 'biweekly',
  releaseExecution: 'investor_finance_team',
  providerRouting: 'not_configured',
  killSwitchActive: false,
  whitelistedPayees: [],
};

export const SHOWCASE_PROJECT_IDS = ['1', '2', '3', '4'] as const;

export interface ShowcaseReport {
  id: string;
  workspaceId: string;
  title: string;
  reportingPeriod: string;
  status: 'finalized';
  artifactAvailable: boolean;
  publishedAt: string;
  deliveryDate: string;
}

/**
 * Report delivery metadata only. The scoped report adapter filters this source
 * through the same active reporting-recipient relationship rule as production.
 */
export const SHOWCASE_REPORTS: readonly ShowcaseReport[] = [
  {
    id: 'showcase-report-quarterly-q2-2026',
    workspaceId: SHOWCASE_WORKSPACE_ID,
    title: 'Q2 2026 Quarterly Report',
    reportingPeriod: 'Q2 2026',
    status: 'finalized',
    artifactAvailable: true,
    publishedAt: '2026-07-15T09:00:00.000Z',
    deliveryDate: '2026-07-15T09:00:00.000Z',
  },
] as const;

export type ShowcaseProjectId = (typeof SHOWCASE_PROJECT_IDS)[number];

export interface ShowcaseGateStory {
  projectId: ShowcaseProjectId;
  sector: string;
  milestoneId: string;
  template: string;
  mode: 'proportional' | 'threshold' | 'tiered';
  state: 'active' | 'blocked' | 'pending_approval' | 'released';
  msi: number;
  allocatedAmount: number;
  releaseAmount: number;
  currency: 'EUR';
  proofStatus: 'verified' | 'missing' | 'in_review';
  signatureStatus: 'complete' | 'pending';
  validatorSummary: string;
};

const NOW = new Date('2026-07-20T10:00:00.000Z');
const PROJECT_MANAGER_ID = 'cbfc74ff-8622-4227-a560-855e16a45e52';
const CLIENT_ID = '532e3ecc-2e17-4be5-a27a-8cd1e47a05a6';
/**
 * Demo account-catalog persona keys. These are DISPLAY/CATALOG identifiers only
 * (LegacyPersonaRoleKey), decoupled from the canonical authority unions —
 * canonical authority for each persona lives in its `account_role` +
 * `scopedRelationships`, never in this key.
 */
type ShowcaseAccountRole = LegacyPersonaRoleKey;

const TOKEN_OPS = 'business_admin' as const;
const TOKEN_WS = 'workspace_admin' as const;

const cloneDate = (value: Date) => new Date(value.getTime());

const cloneUser = (user: User): User =>
  ensureDemoAvatar({
    ...user,
    joinDate: cloneDate(user.joinDate),
    lastActive: cloneDate(user.lastActive),
    permissions: user.permissions ? [...user.permissions] : undefined,
  });

const accountCatalog: Record<ShowcaseAccountRole, User> = {
  platform_owner: {
    id: '0',
    name: 'Tania Platform Owner',
    email: 'owner@ax1.capital',
    role: 'platform_owner',
    account_role: 'platform_owner',
    platform_role: 'platform_owner',
    employmentType: 'internal',
    department: 'AX1 Platform',
    company: 'AX1 Capital',
    joinDate: new Date('2021-01-01'),
    lastActive: NOW,
    status: 'active',
  },
  system_admin: {
    id: '2',
    name: 'Sam System Admin',
    email: 'system.admin@ax1.capital',
    role: 'system_admin',
    account_role: 'platform_admin',
    platform_role: 'system_admin',
    platform_functions: ['operations'],
    platform_capability_assignments: [
      { capability: 'platform.operations.manage', status: 'active', source: 'demo' },
      { capability: 'platform.provisioning.manage', status: 'active', source: 'demo' },
      { capability: 'platform.releases.manage', status: 'active', source: 'demo' },
      { capability: 'platform.customers.manage', status: 'active', source: 'demo' },
      { capability: 'security.integrations.manage', status: 'active', source: 'demo' },
      { capability: 'security.audit.export', status: 'active', source: 'demo' },
      { capability: 'security.api_keys.manage', status: 'active', source: 'demo' },
      { capability: 'system.settings.edit', status: 'active', source: 'demo' },
      { capability: 'security.encryption.rotate', status: 'active', source: 'demo' },
      { capability: 'security.incidents.manage', status: 'active', source: 'demo' },
      { capability: 'security.assurance.download', status: 'active', source: 'demo' },
    ],
    employmentType: 'internal',
    department: 'Platform Engineering',
    company: 'AX1 Capital',
    joinDate: new Date('2023-01-10'),
    lastActive: NOW,
    status: 'active',
  },
  security_admin: {
    id: '10',
    name: 'Sofia Security Admin',
    email: 'security.admin@ax1.capital',
    role: 'security_admin',
    account_role: 'platform_admin',
    platform_role: 'security_admin',
    platform_functions: ['security'],
    platform_capability_assignments: [
      { capability: 'security.identity.manage', status: 'active', source: 'demo' },
      { capability: 'security.audit.export', status: 'active', source: 'demo' },
      { capability: 'security.privacy.manage', status: 'active', source: 'demo' },
      { capability: 'security.api_keys.manage', status: 'active', source: 'demo' },
      { capability: 'security.encryption.manage', status: 'active', source: 'demo' },
      { capability: 'security.encryption.rotate', status: 'active', source: 'demo' },
      { capability: 'security.incidents.manage', status: 'active', source: 'demo' },
      { capability: 'security.compliance.manage', status: 'active', source: 'demo' },
      { capability: 'security.assurance.generate', status: 'active', source: 'demo' },
      { capability: 'security.assurance.download', status: 'active', source: 'demo' },
      { capability: 'platform.access.approve', status: 'active', source: 'demo' },
    ],
    employmentType: 'internal',
    department: 'Security & Assurance',
    company: 'AX1 Capital',
    joinDate: new Date('2024-01-15'),
    lastActive: NOW,
    status: 'active',
  },
  support_admin: {
    id: '11',
    name: 'Noah Support Admin',
    email: 'support.admin@ax1.capital',
    role: 'support_admin',
    account_role: 'support_operator',
    platform_role: 'support_admin',
    platform_functions: ['support'],
    employmentType: 'internal',
    department: 'Customer Support',
    company: 'AX1 Capital',
    joinDate: new Date('2024-04-08'),
    lastActive: NOW,
    status: 'active',
  },
  commercial_admin: {
    id: '12',
    name: 'Maya Commercial Admin',
    email: 'commercial.admin@ax1.capital',
    role: 'commercial_admin',
    account_role: 'platform_admin',
    platform_role: 'commercial_admin',
    platform_functions: ['commercial'],
    platform_capability_assignments: [
      { capability: 'platform.customers.manage', status: 'active', source: 'demo' },
      { capability: 'platform.customers.register', status: 'active', source: 'demo' },
      { capability: 'platform.commercial.manage', status: 'active', source: 'demo' },
    ],
    employmentType: 'internal',
    department: 'Commercial Operations',
    company: 'AX1 Capital',
    joinDate: new Date('2024-05-20'),
    lastActive: NOW,
    status: 'active',
  },
  implementation_admin: {
    id: '13',
    name: 'Leo Implementation Admin',
    email: 'implementation.admin@ax1.capital',
    role: 'implementation_admin',
    account_role: 'platform_admin',
    platform_role: 'implementation_admin',
    platform_functions: ['implementation'],
    platform_capability_assignments: [
      { capability: 'platform.customers.manage', status: 'active', source: 'demo' },
      { capability: 'platform.provisioning.manage', status: 'active', source: 'demo' },
      { capability: 'security.integrations.manage', status: 'active', source: 'demo' },
    ],
    employmentType: 'internal',
    department: 'Implementation',
    company: 'AX1 Capital',
    joinDate: new Date('2024-07-01'),
    lastActive: NOW,
    status: 'active',
  },
  organization_admin: {
    id: '14',
    name: 'Olivia Martin',
    email: 'organisation.admin@northstarholdings.eu',
    role: 'organization_admin',
    account_role: 'organization_owner',
    workspace_id: SHOWCASE_WORKSPACE_ID,
    workspace_role: 'organization_admin',
    employmentType: 'internal',
    department: 'Corporate Administration',
    company: 'Northstar Holdings',
    joinDate: new Date('2023-03-01'),
    lastActive: NOW,
    status: 'active',
  },
  customer_security_admin: {
    id: '15',
    name: 'Elias Customer Security Admin',
    email: 'security.admin@northstarholdings.eu',
    role: 'customer_security_admin',
    account_role: 'organization_admin',
    workspace_id: SHOWCASE_WORKSPACE_ID,
    workspace_role: 'customer_security_admin',
    employmentType: 'internal',
    department: 'Information Security',
    company: 'Northstar Holdings',
    joinDate: new Date('2023-04-12'),
    lastActive: NOW,
    status: 'active',
  },
  [TOKEN_OPS]: {
    id: '1',
    name: 'Bianca Rossi',
    email: 'business.admin@northstarholdings.eu',
    role: TOKEN_OPS,
    account_role: 'organization_admin',
    workspace_id: SHOWCASE_WORKSPACE_ID,
    workspace_role: TOKEN_OPS,
    employmentType: 'internal',
    department: 'Portfolio Operations',
    company: 'Northstar Holdings',
    joinDate: new Date('2022-02-20'),
    lastActive: NOW,
    status: 'active',
  },
  [TOKEN_WS]: {
    id: '16',
    name: 'William Workspace Operator',
    email: 'workspace.admin@northstarholdings.eu',
    role: TOKEN_WS,
    account_role: 'organization_member',
    workspace_id: SHOWCASE_WORKSPACE_ID,
    workspace_role: TOKEN_WS,
    employmentType: 'internal',
    department: 'Programme Operations',
    company: 'Northstar Holdings',
    joinDate: new Date('2023-05-22'),
    lastActive: NOW,
    status: 'active',
    // Canonical workspace-scoped administrator: an organization_member with an
    // active workspace_member relationship (Model A). This grants Stage-1
    // SURFACE entry only — resource access still requires explicit
    // project/company/investment relationships, added separately when needed.
    scopedRelationships: [
      {
        type: 'workspace_member',
        resourceType: 'workspace',
        resourceId: SHOWCASE_WORKSPACE_ID,
        accessLevel: 'manage',
        status: 'active',
      },
    ],
  },
};

/** Investment Rachel (the showcase investor representative) is entitled to. */
export const SHOWCASE_INVESTOR_INVESTMENT_ID = 'showcase-investment-1';

/**
 * Authoritative investment→project association for the showcase dataset.
 *
 * Investors hold `investor_representative` relationships scoped to an
 * investment id; their project visibility is derived exclusively through this
 * link (mirrors `Investment.projectId` in the canonical repository). Never
 * attach a `project` resource id directly to an investor relationship.
 */
export const SHOWCASE_INVESTMENT_PROJECT_LINKS: Readonly<Record<string, ShowcaseProjectId>> = {
  'showcase-investment-1': '1',
  'showcase-investment-2': '2',
  'showcase-investment-3': '3',
  'showcase-investment-4': '4',
};

/** Resolve the project ids authoritatively linked to a showcase investment. */
export const resolveShowcaseProjectIdsForInvestment = (investmentId: string): ShowcaseProjectId[] => {
  const projectId = SHOWCASE_INVESTMENT_PROJECT_LINKS[investmentId];
  return projectId ? [projectId] : [];
};

// Scoped-relationship demo personas.
//
// These users are `organization_member` account holders (no organization-wide
// authority). Their access to the four showcase projects comes exclusively
// from the structured `scopedRelationships` records below, which are the
// same shape the canonical resolver (`lib/auth/scopedRelationshipResolver.ts`)
// consumes in production. Nothing here is a label or a comment — every
// project-visibility decision made about these personas is computed from
// this data.
const scopedPersonaCatalog: Record<string, User> = {
  project_manager: {
    id: PROJECT_MANAGER_ID,
    name: 'Sarah Johnson',
    email: 'sarah.johnson@northstarholdings.eu',
    role: 'member',
    account_role: 'organization_member',
    workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'internal',
    department: 'Programme Delivery',
    company: 'Northstar Holdings',
    joinDate: new Date('2022-03-10'),
    lastActive: NOW,
    status: 'active',
    scopedRelationships: [
      // Workspace SURFACE membership (enter the workspace shell) — separate from
      // the resource relationship below (access to the specific projects).
      { type: 'workspace_member', resourceType: 'workspace', resourceId: SHOWCASE_WORKSPACE_ID, accessLevel: 'contribute', status: 'active' },
      ...SHOWCASE_PROJECT_IDS.map((projectId): UserScopedRelationship => ({
        type: 'project_manager',
        resourceType: 'project',
        resourceId: projectId,
        accessLevel: 'manage',
        status: 'active',
      })),
    ],
  },
  partner_representative: {
    id: '7',
    name: 'Lisa Thompson',
    email: 'lisa.thompson@meridianadvisory.eu',
    role: 'member',
    account_role: 'organization_member',
    workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'external',
    department: 'Programme Assurance',
    company: 'Meridian Advisory',
    joinDate: new Date('2022-11-15'),
    lastActive: NOW,
    status: 'active',
    scopedRelationships: [
      // Limited external workspace surface membership.
      { type: 'workspace_member', resourceType: 'workspace', resourceId: SHOWCASE_WORKSPACE_ID, accessLevel: 'view', status: 'active' },
      // Company-anchored partner representation (invitation authority shape).
      {
        type: 'partner_representative',
        resourceType: 'company',
        resourceId: 'showcase-company-partner',
        accessLevel: 'contribute',
        status: 'active',
        representedCompanyId: 'showcase-company-partner',
      },
      // Deliberate assigned-work fixture. Company representation alone never
      // implies Project access; this separate relationship is what makes the
      // Partner dashboard useful and keeps the authorization boundary honest.
      {
        type: 'partner_representative',
        resourceType: 'project',
        resourceId: '1',
        accessLevel: 'contribute',
        status: 'active',
        representedCompanyId: 'showcase-company-partner',
      },
    ],
  },
  vendor_representative: {
    id: '5',
    name: 'Emily Vendor Lead',
    email: 'emily@atlasdelivery.eu',
    role: 'member',
    account_role: 'organization_member',
    workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'external',
    department: 'Delivery',
    company: 'Atlas Delivery Partners',
    joinDate: new Date('2022-05-01'),
    lastActive: NOW,
    status: 'active',
    scopedRelationships: [
      // Limited external workspace surface membership.
      { type: 'workspace_member', resourceType: 'workspace', resourceId: SHOWCASE_WORKSPACE_ID, accessLevel: 'view', status: 'active' },
      // Company-anchored vendor representation (invitation authority shape).
      {
        type: 'vendor_representative',
        resourceType: 'company',
        resourceId: 'showcase-company-vendor',
        accessLevel: 'contribute',
        status: 'active',
        representedCompanyId: 'showcase-company-vendor',
      },
    ],
  },
  investor_representative: {
    id: '8',
    name: 'Rachel Nguyen',
    email: 'rachel.nguyen@horizoncapital.eu',
    role: 'member',
    account_role: 'organization_member',
    workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'external',
    department: 'Investment Committee',
    company: 'Horizon Capital Partners',
    joinDate: new Date('2024-06-01'),
    lastActive: NOW,
    status: 'active',
    // Rachel is an investor representative scoped to a specific INVESTMENT,
    // never directly to a project. Her project visibility is derived through
    // the authoritative investment→project association
    // (SHOWCASE_INVESTMENT_PROJECT_LINKS). This proves investor access does
    // not leak across projects and is mediated only by the investment link.
    scopedRelationships: [
      {
        type: 'investor_representative',
        resourceType: 'investment',
        resourceId: SHOWCASE_INVESTOR_INVESTMENT_ID,
        accessLevel: 'view',
        status: 'active',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Canonical scoped-relationship matrix coverage. Each persona carries ONE
  // executable relationship record so the browser/test matrix can exercise
  // every ScopedRelationshipType independently. All are `organization_member`
  // account holders with NO organization-wide authority — access is derived
  // solely from the record below.
  // ─────────────────────────────────────────────────────────────────────────
  workspace_member: {
    id: '20', name: 'Wendy Workspace Member', email: 'wendy.member@northstarholdings.eu',
    role: 'member', account_role: 'organization_member', workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'internal', department: 'Operations', company: 'Northstar Holdings',
    joinDate: new Date('2023-02-01'), lastActive: NOW, status: 'active',
    scopedRelationships: [{ type: 'workspace_member', resourceType: 'workspace', resourceId: SHOWCASE_WORKSPACE_ID, accessLevel: 'contribute', status: 'active' }],
  },
  company_representative: {
    // Maria López — company_representative of Meridian Advisory.
    // Sidebar experience labels follow company↔project participation in context:
    //   project 2 (Harbour District) → Investee Portal / Investee Representative
    //   other projects (route) → Partner Portal / Partner Representative
    //   company representation only (no project scope / route) → Company Portal fallback
    // Default operating context is Harbour District (project-scoped representation)
    // so company-portal dashboard/home also resolves Investee labels.
    id: '21',
    name: 'Maria López',
    email: 'maria.lopez@meridianadvisory.eu',
    role: 'member',
    account_role: 'organization_member',
    workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'external',
    department: 'Advisory',
    company: 'Meridian Advisory',
    joinDate: new Date('2023-03-01'),
    lastActive: NOW,
    status: 'active',
    scopedRelationships: [
      // Workspace SURFACE membership (enter shell) — separate from company representation.
      {
        type: 'workspace_member',
        resourceType: 'workspace',
        resourceId: SHOWCASE_WORKSPACE_ID,
        accessLevel: 'view',
        status: 'active',
      },
      {
        type: 'company_representative',
        resourceType: 'company',
        resourceId: 'showcase-company-partner',
        accessLevel: 'view',
        status: 'active',
        representedCompanyId: 'showcase-company-partner',
      },
      // Operating project context for sidebar participation labels when the
      // route has no `/projects/:id` (dashboard / home). Meridian is
      // capital_recipient on Harbour District → Investee Portal.
      {
        type: 'company_representative',
        resourceType: 'project',
        resourceId: '2',
        accessLevel: 'view',
        status: 'active',
        representedCompanyId: 'showcase-company-partner',
      },
    ],
  },
  project_contributor: {
    id: '22', name: 'Priya Project Contributor', email: 'priya.contributor@atlasdelivery.eu',
    role: 'member', account_role: 'organization_member', workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'external', department: 'Delivery', company: 'Atlas Delivery Partners',
    joinDate: new Date('2023-04-01'), lastActive: NOW, status: 'active',
    scopedRelationships: [{ type: 'workspace_member', resourceType: 'workspace', resourceId: SHOWCASE_WORKSPACE_ID, accessLevel: 'view', status: 'active' }, { type: 'project_contributor', resourceType: 'project', resourceId: '1', accessLevel: 'contribute', status: 'active' }],
  },
  investment_manager: {
    id: '23', name: 'Ingrid Investment Manager', email: 'ingrid.manager@horizoncapital.eu',
    role: 'member', account_role: 'organization_member', workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'external', department: 'Investment Management', company: 'Horizon Capital Partners',
    joinDate: new Date('2023-05-01'), lastActive: NOW, status: 'active',
    scopedRelationships: [{ type: 'investment_manager', resourceType: 'investment', resourceId: SHOWCASE_INVESTOR_INVESTMENT_ID, accessLevel: 'manage', status: 'active' }],
  },
  reporting_recipient: {
    id: '24', name: 'Ravi Reporting Recipient', email: 'ravi.reports@horizoncapital.eu',
    role: 'member', account_role: 'organization_member', workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'external', department: 'LP Reporting', company: 'Horizon Capital Partners',
    joinDate: new Date('2023-06-01'), lastActive: NOW, status: 'active',
    scopedRelationships: [{ type: 'reporting_recipient', resourceType: 'report', resourceId: 'showcase-report-quarterly-q2-2026', accessLevel: 'view', status: 'active' }],
  },
  decision_participant: {
    id: '25', name: 'Dana Decision Participant', email: 'dana.decision@northstarholdings.eu',
    role: 'member', account_role: 'organization_member', workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'internal', department: 'Governance', company: 'Northstar Holdings',
    joinDate: new Date('2023-07-01'), lastActive: NOW, status: 'active',
    scopedRelationships: [{ type: 'decision_participant', resourceType: 'decision', resourceId: 'showcase-decision-gate-1', accessLevel: 'review', status: 'active' }],
  },
  validator: {
    id: '26', name: 'Victor Validator', email: 'victor.validator@independentassurance.eu',
    role: 'member', account_role: 'organization_member', workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'external', department: 'Independent Assurance', company: 'Independent Assurance Ltd',
    joinDate: new Date('2023-08-01'), lastActive: NOW, status: 'active',
    // Validator is object-scoped only — exact Task target, no project-wide role.
    scopedRelationships: [
      { type: 'workspace_member', resourceType: 'workspace', resourceId: SHOWCASE_WORKSPACE_ID, accessLevel: 'view', status: 'active' },
      { type: 'validator', resourceType: 'task', resourceId: 'task-1-certificate', accessLevel: 'review', status: 'active' },
    ],
  },
  validator_gate: {
    id: '28', name: 'Gita Gate Validator', email: 'gita.gate@independentassurance.eu',
    role: 'member', account_role: 'organization_member', workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'external', department: 'Independent Assurance', company: 'Independent Assurance Ltd',
    joinDate: new Date('2023-08-05'), lastActive: NOW, status: 'active',
    scopedRelationships: [
      { type: 'workspace_member', resourceType: 'workspace', resourceId: SHOWCASE_WORKSPACE_ID, accessLevel: 'view', status: 'active' },
      { type: 'validator', resourceType: 'gate', resourceId: 'milestone-1-gate', accessLevel: 'review', status: 'active' },
    ],
  },
  validator_milestone: {
    id: '29', name: 'Mira Milestone Validator', email: 'mira.milestone@independentassurance.eu',
    role: 'member', account_role: 'organization_member', workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'external', department: 'Independent Assurance', company: 'Independent Assurance Ltd',
    joinDate: new Date('2023-08-08'), lastActive: NOW, status: 'active',
    scopedRelationships: [
      { type: 'workspace_member', resourceType: 'workspace', resourceId: SHOWCASE_WORKSPACE_ID, accessLevel: 'view', status: 'active' },
      { type: 'validator', resourceType: 'milestone', resourceId: 'milestone-1-gate', accessLevel: 'review', status: 'active' },
    ],
  },
  // Alias for demo role switcher: unassigned Workspace Member → Welcome
  unassigned_member: {
    id: '20', name: 'Wendy Workspace Member', email: 'wendy.member@northstarholdings.eu',
    role: 'member', account_role: 'organization_member', workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'internal', department: 'Operations', company: 'Northstar Holdings',
    joinDate: new Date('2023-02-01'), lastActive: NOW, status: 'active',
    scopedRelationships: [{ type: 'workspace_member', resourceType: 'workspace', resourceId: SHOWCASE_WORKSPACE_ID, accessLevel: 'contribute', status: 'active' }],
  },
  observer: {
    id: '27', name: 'Oscar Observer', email: 'oscar.observer@northstarholdings.eu',
    role: 'member', account_role: 'organization_member', workspace_id: SHOWCASE_WORKSPACE_ID,
    employmentType: 'internal', department: 'Audit', company: 'Northstar Holdings',
    joinDate: new Date('2023-09-01'), lastActive: NOW, status: 'active',
    scopedRelationships: [
      { type: 'workspace_member', resourceType: 'workspace', resourceId: SHOWCASE_WORKSPACE_ID, accessLevel: 'view', status: 'active' },
      { type: 'observer', resourceType: 'project', resourceId: '3', accessLevel: 'view', status: 'active' },
    ],
  },
};

/** Lookup by canonical scoped-relationship key. */
export const scopedRelationshipPersonas: Record<string, User> = Object.fromEntries(
  Object.entries(scopedPersonaCatalog).map(([key, user]) => [key, ensureDemoAvatar(user)]),
);

export const createShowcaseAccounts = (): Record<ShowcaseAccountRole, User> =>
  Object.fromEntries(
    Object.entries(accountCatalog).map(([role, user]) => [role, cloneUser(user)]),
  ) as Record<ShowcaseAccountRole, User>;

export const createShowcaseScopedPersonas = (): User[] =>
  Object.values(scopedPersonaCatalog).map(cloneUser);

export const createShowcaseUsers = (): User[] => [
  ...Object.values(createShowcaseAccounts()),
  ...createShowcaseScopedPersonas(),
];

export const SHOWCASE_PRIMARY_CLIENTS: Record<ShowcaseProjectId, { id: string; name: string }> = {
  '1': { id: 'showcase-company-infra', name: 'NorthLink Infrastructure SPV' },
  '2': { id: 'showcase-company-real-estate', name: 'Harbour District Development' },
  '3': { id: 'showcase-company-climate', name: 'GreenLoop Industries' },
  '4': { id: 'showcase-company-growth', name: 'NovaScale Technologies' },
};

const projectCatalog: Project[] = [
  {
    id: '1',
    name: 'NorthLink Regional Infrastructure Programme',
    description: 'A milestone-governed transport corridor programme with independent engineering validation and proportional capital release.',
    type: 'development',
    status: 'active',
    priority: 'critical',
    startDate: new Date('2026-01-12'),
    endDate: new Date('2027-12-18'),
    progress: 68,
    budget: 120_000_000,
    spent: 51_600_000,
    clientId: CLIENT_ID,
    primaryClientCompany: SHOWCASE_PRIMARY_CLIENTS['1'],
    client: SHOWCASE_PRIMARY_CLIENTS['1'].name,
    managerId: PROJECT_MANAGER_ID,
    teamMembers: [PROJECT_MANAGER_ID, '5', '7', '8', CLIENT_ID],
    milestones: [],
    documents: [],
    healthScore: 88,
    health: 'good',
    riskLevel: 'medium',
    riskFactors: ['Independent engineering certificate due before final drawdown'],
    blockers: [],
    tasksCompleted: 3,
    totalTasks: 4,
    createdAt: new Date('2025-12-15'),
    updatedAt: NOW,
  },
  {
    id: '2',
    name: 'Harbour District Mixed-Use Development',
    description: 'A real-estate development where a capital Gate is held until compliance proof and contractor evidence are accepted.',
    type: 'development',
    status: 'active',
    priority: 'high',
    startDate: new Date('2026-03-02'),
    endDate: new Date('2028-09-30'),
    progress: 47,
    budget: 85_000_000,
    spent: 24_650_000,
    clientId: CLIENT_ID,
    primaryClientCompany: SHOWCASE_PRIMARY_CLIENTS['2'],
    client: SHOWCASE_PRIMARY_CLIENTS['2'].name,
    managerId: PROJECT_MANAGER_ID,
    teamMembers: [PROJECT_MANAGER_ID, '5', '7', '8', CLIENT_ID],
    milestones: [],
    documents: [],
    healthScore: 61,
    health: 'at_risk',
    riskLevel: 'high',
    riskFactors: ['Fire-safety certificate not yet validated', 'Mobilisation evidence requires resubmission'],
    blockers: ['Gate G-02 is blocked pending compliance proof'],
    tasksCompleted: 2,
    totalTasks: 4,
    createdAt: new Date('2026-01-22'),
    updatedAt: NOW,
  },
  {
    id: '3',
    name: 'GreenLoop Industrial Decarbonisation Facility',
    description: 'An impact-capital facility using live MRV data, third-party validation, and signatures before its first release.',
    type: 'manufacturing',
    status: 'planning',
    priority: 'high',
    startDate: new Date('2026-05-04'),
    endDate: new Date('2027-12-17'),
    progress: 28,
    budget: 42_000_000,
    spent: 4_200_000,
    clientId: CLIENT_ID,
    primaryClientCompany: SHOWCASE_PRIMARY_CLIENTS['3'],
    client: SHOWCASE_PRIMARY_CLIENTS['3'].name,
    managerId: PROJECT_MANAGER_ID,
    teamMembers: [PROJECT_MANAGER_ID, '5', '7', '8', CLIENT_ID],
    milestones: [],
    documents: [],
    healthScore: 76,
    health: 'fair',
    riskLevel: 'medium',
    riskFactors: ['MRV gateway calibration in progress', 'Investor signature pending'],
    blockers: [],
    tasksCompleted: 1,
    totalTasks: 4,
    createdAt: new Date('2026-03-18'),
    updatedAt: NOW,
  },
  {
    id: '4',
    name: 'NovaScale European Expansion',
    description: 'A completed growth-capital programme demonstrating verified commercial outcomes, release settlement, and immutable audit history.',
    type: 'go_to_market',
    status: 'completed',
    priority: 'medium',
    startDate: new Date('2025-09-01'),
    endDate: new Date('2026-06-30'),
    progress: 100,
    budget: 18_000_000,
    spent: 17_100_000,
    clientId: CLIENT_ID,
    primaryClientCompany: SHOWCASE_PRIMARY_CLIENTS['4'],
    client: SHOWCASE_PRIMARY_CLIENTS['4'].name,
    managerId: PROJECT_MANAGER_ID,
    teamMembers: [PROJECT_MANAGER_ID, '5', '7', '8', CLIENT_ID],
    milestones: [],
    documents: [],
    healthScore: 96,
    health: 'excellent',
    riskLevel: 'low',
    riskFactors: [],
    blockers: [],
    tasksCompleted: 4,
    totalTasks: 4,
    createdAt: new Date('2025-07-14'),
    updatedAt: new Date('2026-07-01'),
  },
];

const task = (
  input: Pick<Task, 'id' | 'projectId' | 'milestoneId' | 'title' | 'description' | 'status' | 'priority' | 'assigneeId' | 'dueDate'> &
    Partial<Task>,
): Task => ({
  estimatedHours: 16,
  tags: ['governance'],
  attachments: [],
  comments: [],
  visibility: 'partner',
  createdAt: new Date('2026-06-01'),
  updatedAt: NOW,
  ...input,
});

const taskCatalog: Task[] = [
  task({ id: 'task-1-survey', projectId: '1', milestoneId: 'milestone-1-gate', title: 'Approve geotechnical survey', description: 'Independent survey confirms foundation readiness.', status: 'completed', priority: 'high', assigneeId: '7', dueDate: new Date('2026-06-18'), capital_relevant: true, governance_role: 'required', verification_status: 'approved', verified_by: '7', verified_at: '2026-06-18T15:20:00.000Z', validator_id: '7' }),
  task({ id: 'task-1-permits', projectId: '1', milestoneId: 'milestone-1-gate', title: 'Validate construction permits', description: 'Permit pack is matched to the approved works package.', status: 'completed', priority: 'critical', assigneeId: PROJECT_MANAGER_ID, dueDate: new Date('2026-06-28'), capital_relevant: true, governance_role: 'blocker', verification_status: 'approved', verified_by: '7', verified_at: '2026-06-28T12:05:00.000Z', validator_id: '7' }),
  task({ id: 'task-1-progress', projectId: '1', milestoneId: 'milestone-1-gate', title: 'Verify foundation progress', description: 'Measured delivery contributes 60% of the Gate score.', status: 'completed', priority: 'high', assigneeId: '5', dueDate: new Date('2026-07-12'), measurement_mode: 'quantity', unit: '%', quantity_planned: 100, quantity_completed: 93, quantity_verified: 93, capital_relevant: true, governance_role: 'weighted', governance_weight: 60, verification_status: 'approved', verified_by: '7', verified_at: '2026-07-13T09:00:00.000Z', validator_id: '7' }),
  task({ id: 'task-1-certificate', projectId: '1', milestoneId: 'milestone-1-gate', title: 'Issue engineer release certificate', description: 'Final approval package for proportional drawdown.', status: 'review', priority: 'critical', assigneeId: '7', dueDate: new Date('2026-07-22'), capital_relevant: true, governance_role: 'required', verification_status: 'pending', validator_id: '7' }),

  task({ id: 'task-2-title', projectId: '2', milestoneId: 'milestone-2-gate', title: 'Complete title due diligence', description: 'Legal title report and encumbrance review.', status: 'completed', priority: 'critical', assigneeId: '7', dueDate: new Date('2026-05-15'), capital_relevant: true, governance_role: 'required', verification_status: 'approved', verified_by: '7', verified_at: '2026-05-15T16:00:00.000Z', validator_id: '7' }),
  task({ id: 'task-2-zoning', projectId: '2', milestoneId: 'milestone-2-gate', title: 'Confirm zoning approval', description: 'Planning authority approval is recorded as Gate proof.', status: 'completed', priority: 'critical', assigneeId: PROJECT_MANAGER_ID, dueDate: new Date('2026-06-20'), capital_relevant: true, governance_role: 'blocker', verification_status: 'approved', verified_by: '7', verified_at: '2026-06-21T08:30:00.000Z', validator_id: '7' }),
  task({ id: 'task-2-mobilisation', projectId: '2', milestoneId: 'milestone-2-gate', title: 'Verify contractor mobilisation', description: 'Resubmit time-stamped site evidence and equipment schedule.', status: 'review', priority: 'high', assigneeId: '5', dueDate: new Date('2026-07-18'), capital_relevant: true, governance_role: 'weighted', governance_weight: 40, verification_status: 'rejected', verified_by: '7', verified_at: '2026-07-19T10:15:00.000Z', validator_id: '7' }),
  // Deliberate Investee delivery assignment. This does not make Maria a validator.
  task({ id: 'task-2-fire', projectId: '2', milestoneId: 'milestone-2-gate', title: 'Upload fire-safety certificate', description: 'Obtain the issued certificate, check the site and inspection date, then prepare the exact document version for independent review. The existing request PDF is supporting material, not an issued certificate.', status: 'in_progress', priority: 'critical', assigneeId: '21', dueDate: new Date('2026-07-24'), capital_relevant: true, governance_role: 'blocker', verification_status: 'pending', validator_id: '7' }),

  task({ id: 'task-3-baseline', projectId: '3', milestoneId: 'milestone-3-gate', title: 'Approve emissions baseline', description: 'Audited baseline establishes the impact KPI denominator.', status: 'completed', priority: 'high', assigneeId: '7', dueDate: new Date('2026-06-30'), capital_relevant: true, governance_role: 'required', verification_status: 'approved', verified_by: '7', verified_at: '2026-07-01T11:00:00.000Z', validator_id: '7' }),
  task({ id: 'task-3-mrv', projectId: '3', milestoneId: 'milestone-3-gate', title: 'Connect MRV metric gateway', description: 'Stream energy and emissions metrics into the MSI calculation.', status: 'in_progress', priority: 'critical', assigneeId: '5', dueDate: new Date('2026-07-26'), capital_relevant: true, governance_role: 'weighted', governance_weight: 55, verification_status: 'pending', validator_id: '7' }),
  task({ id: 'task-3-validator', projectId: '3', milestoneId: 'milestone-3-gate', title: 'Appoint independent impact validator', description: 'Record authority, scope, and conflict declaration.', status: 'review', priority: 'high', assigneeId: PROJECT_MANAGER_ID, dueDate: new Date('2026-07-23'), capital_relevant: true, governance_role: 'required', verification_status: 'pending', validator_id: '7' }),
  task({ id: 'task-3-signature', projectId: '3', milestoneId: 'milestone-3-gate', title: 'Collect investor Gate signature', description: 'Investment committee signature completes approval authority.', status: 'todo', priority: 'critical', assigneeId: '8', dueDate: new Date('2026-07-29'), capital_relevant: true, governance_role: 'blocker', verification_status: 'pending', validator_id: '8' }),

  task({ id: 'task-4-market', projectId: '4', milestoneId: 'milestone-4-gate', title: 'Validate market-entry plan', description: 'Country sequencing and launch budget approved.', status: 'completed', priority: 'high', assigneeId: PROJECT_MANAGER_ID, dueDate: new Date('2026-02-20'), capital_relevant: true, governance_role: 'required', verification_status: 'approved', verified_by: '7', verified_at: '2026-02-20T12:00:00.000Z', validator_id: '7' }),
  task({ id: 'task-4-partners', projectId: '4', milestoneId: 'milestone-4-gate', title: 'Complete channel partner onboarding', description: 'Signed partner agreements and enablement evidence.', status: 'completed', priority: 'high', assigneeId: '7', dueDate: new Date('2026-04-15'), capital_relevant: true, governance_role: 'weighted', governance_weight: 35, verification_status: 'approved', verified_by: '7', verified_at: '2026-04-15T14:10:00.000Z', validator_id: '7' }),
  task({ id: 'task-4-revenue', projectId: '4', milestoneId: 'milestone-4-gate', title: 'Verify recurring revenue threshold', description: 'CRM-sourced performance validates the commercial Gate.', status: 'completed', priority: 'critical', assigneeId: '5', dueDate: new Date('2026-06-15'), capital_relevant: true, governance_role: 'weighted', governance_weight: 65, verification_status: 'approved', verified_by: '7', verified_at: '2026-06-16T08:45:00.000Z', validator_id: '7' }),
  task({ id: 'task-4-release', projectId: '4', milestoneId: 'milestone-4-gate', title: 'Approve and settle final release', description: 'Signatures, release instruction, and settlement receipt archived.', status: 'completed', priority: 'critical', assigneeId: '8', dueDate: new Date('2026-06-30'), capital_relevant: true, governance_role: 'blocker', verification_status: 'approved', verified_by: '8', verified_at: '2026-06-30T17:00:00.000Z', validator_id: '8' }),
];

const milestoneCatalog: Milestone[] = [
  // An unconfigured entry into the Journey, intentionally absent from Gate
  // stories so visiting it never inherits an active policy or release amount.
  {
    id: SHOWCASE_BLANK_MILESTONE_ID,
    projectId: '1',
    name: 'Capital Gate Journey — blank milestone',
    description: '',
    dueDate: new Date('2026-12-31T12:00:00.000Z'),
    status: 'upcoming',
    stageStatus: 'Not Started',
    progress: 0,
    tasks: [],
    deliverables: [],
    assignedUsers: [],
    createdAt: NOW,
    updatedAt: NOW,
  },
  { id: 'milestone-1-gate', projectId: '1', name: 'Foundation works Capital Gate', description: 'Release against verified permits, works progress, and engineer certification.', startDate: new Date('2026-05-01'), dueDate: new Date('2026-07-22'), status: 'active', stageStatus: 'In Review', progress: 93, tasks: [], deliverables: ['Permit pack', 'Progress certificate', 'Engineer release certificate'], assignedUsers: [PROJECT_MANAGER_ID, '7'], createdAt: new Date('2026-04-20'), updatedAt: NOW },
  { id: 'milestone-2-gate', projectId: '2', name: 'Site mobilisation Capital Gate', description: 'A blocked threshold Gate requiring complete compliance proof.', startDate: new Date('2026-05-01'), dueDate: new Date('2026-07-24'), status: 'active', stageStatus: 'Blocked', progress: 58, tasks: [], deliverables: ['Title report', 'Zoning approval', 'Mobilisation evidence', 'Fire-safety certificate'], assignedUsers: [PROJECT_MANAGER_ID, '5', '7'], createdAt: new Date('2026-04-25'), updatedAt: NOW },
  { id: 'milestone-3-gate', projectId: '3', name: 'Impact baseline Capital Gate', description: 'A tiered Gate combining live MRV metrics, independent validation, and investor signature.', startDate: new Date('2026-06-01'), dueDate: new Date('2026-07-29'), status: 'active', stageStatus: 'In Progress', progress: 72, tasks: [], deliverables: ['Emissions baseline', 'MRV data connection', 'Validator declaration', 'Investor signature'], assignedUsers: [PROJECT_MANAGER_ID, '7', '8'], createdAt: new Date('2026-05-20'), updatedAt: NOW },
  { id: 'milestone-4-gate', projectId: '4', name: 'European expansion Capital Gate', description: 'Completed commercial Gate with verified CRM metrics, signatures, settlement, and audit trail.', startDate: new Date('2026-01-15'), dueDate: new Date('2026-06-30'), status: 'completed', stageStatus: 'Complete', progress: 100, tasks: [], deliverables: ['Market-entry plan', 'Partner agreements', 'CRM performance export', 'Settlement receipt'], assignedUsers: [PROJECT_MANAGER_ID, '7', '8'], createdAt: new Date('2026-01-05'), updatedAt: new Date('2026-06-30') },
];

export const SHOWCASE_GATE_STORIES: ShowcaseGateStory[] = [
  { projectId: '1', sector: 'Infrastructure & projects', milestoneId: 'milestone-1-gate', template: 'Construction progress & certification', mode: 'proportional', state: 'active', msi: 93, allocatedAmount: 8_000_000, releaseAmount: 7_440_000, currency: 'EUR', proofStatus: 'verified', signatureStatus: 'complete', validatorSummary: 'Independent engineer + Project Manager' },
  { projectId: '2', sector: 'Real estate development', milestoneId: 'milestone-2-gate', template: 'Site mobilisation & compliance', mode: 'threshold', state: 'blocked', msi: 58, allocatedAmount: 5_000_000, releaseAmount: 0, currency: 'EUR', proofStatus: 'missing', signatureStatus: 'pending', validatorSummary: 'Compliance validator + Workspace Admin' },
  { projectId: '3', sector: 'Climate & impact capital', milestoneId: 'milestone-3-gate', template: 'Impact baseline & MRV', mode: 'tiered', state: 'pending_approval', msi: 72, allocatedAmount: 4_000_000, releaseAmount: 2_800_000, currency: 'EUR', proofStatus: 'in_review', signatureStatus: 'pending', validatorSummary: 'Impact verifier + Investor' },
  { projectId: '4', sector: 'Growth & expansion capital', milestoneId: 'milestone-4-gate', template: 'Commercial expansion', mode: 'threshold', state: 'released', msi: 96, allocatedAmount: 3_000_000, releaseAmount: 3_000_000, currency: 'EUR', proofStatus: 'verified', signatureStatus: 'complete', validatorSummary: 'Commercial validator + Investor' },
];

const deliverableCatalog: Record<string, MilestoneDeliverable[]> = {
  'milestone-1-gate': [
    { id: 'deliverable-1-1', type: 'File', title: 'Independent engineer progress certificate.pdf', fileId: 'file-infra-certificate', description: 'Signed evidence supporting the proportional release.', position: 0, createdAt: new Date('2026-07-13') },
  ],
  'milestone-2-gate': [
    { id: 'deliverable-2-1', type: 'File', title: 'Mobilisation evidence — resubmission required.pdf', fileId: 'file-real-estate-mobilisation', description: 'Rejected proof retained in the audit trail.', position: 0, createdAt: new Date('2026-07-18') },
  ],
  'milestone-3-gate': [
    { id: 'deliverable-3-1', type: 'Link', title: 'MRV metric gateway', url: 'https://example.com/showcase/mrv', description: 'Live metric source for the Gate score.', position: 0, createdAt: new Date('2026-07-10') },
  ],
  'milestone-4-gate': [
    { id: 'deliverable-4-1', type: 'File', title: 'Capital release settlement receipt.pdf', fileId: 'file-growth-settlement', description: 'Final settlement evidence and audit record.', position: 0, createdAt: new Date('2026-06-30') },
  ],
};

const cloneTask = (item: Task): Task => ({
  ...item,
  dueDate: cloneDate(item.dueDate),
  createdAt: cloneDate(item.createdAt),
  updatedAt: cloneDate(item.updatedAt),
  tags: [...item.tags],
  attachments: [...item.attachments],
  comments: [...item.comments],
});

export const createShowcaseTasks = (): Task[] => taskCatalog.map(cloneTask);

export const createShowcaseMilestones = (): Milestone[] => {
  const tasks = createShowcaseTasks();
  return milestoneCatalog.map((milestone) => ({
    ...milestone,
    startDate: milestone.startDate ? cloneDate(milestone.startDate) : undefined,
    dueDate: cloneDate(milestone.dueDate),
    createdAt: cloneDate(milestone.createdAt),
    updatedAt: milestone.updatedAt ? cloneDate(milestone.updatedAt) : undefined,
    tasks: tasks.filter((item) => item.milestoneId === milestone.id),
    deliverables: [...milestone.deliverables],
    assignedUsers: milestone.assignedUsers ? [...milestone.assignedUsers] : undefined,
  }));
};

export const createShowcaseProjects = (): Project[] => {
  const milestones = createShowcaseMilestones();
  return projectCatalog.map((project) => ({
    ...project,
    startDate: cloneDate(project.startDate),
    endDate: project.endDate ? cloneDate(project.endDate) : undefined,
    createdAt: cloneDate(project.createdAt),
    updatedAt: cloneDate(project.updatedAt),
    primaryClientCompany: project.primaryClientCompany ? { ...project.primaryClientCompany } : undefined,
    teamMembers: [...project.teamMembers],
    milestones: milestones.filter((milestone) => milestone.projectId === project.id),
    documents: [...project.documents],
    riskFactors: project.riskFactors ? [...project.riskFactors] : undefined,
    blockers: project.blockers ? [...project.blockers] : undefined,
  }));
};

export const createShowcaseMilestoneDeliverables = (): Record<string, MilestoneDeliverable[]> =>
  Object.fromEntries(
    Object.entries(deliverableCatalog).map(([milestoneId, deliverables]) => [
      milestoneId,
      deliverables.map((deliverable) => ({
        ...deliverable,
        createdAt: cloneDate(deliverable.createdAt),
        updatedAt: deliverable.updatedAt ? cloneDate(deliverable.updatedAt) : undefined,
      })),
    ]),
  );

export const getShowcaseProject = (projectId: string): Project | null =>
  createShowcaseProjects().find((project) => project.id === projectId) ?? null;

export const getShowcaseTasksForProject = (projectId: string): Task[] =>
  createShowcaseTasks().filter((item) => item.projectId === projectId);

export const getShowcaseMilestonesForProject = (projectId: string): Milestone[] =>
  createShowcaseMilestones().filter((item) => item.projectId === projectId);
